import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { Globe, Plus, Trash2, Copy, Check, X, Eye, EyeOff, PauseCircle, PlayCircle, Settings as SettingsIcon } from "lucide-react";

// Admin → Domains. Lives at /domains in the Notify SPA, surfaced only
// to super_admin / admin in portal.tsx. One row per (this tenant,
// brand domain) that's authenticated at Brevo. Each row carries that
// Brevo account's API key + signing secret (write-only over the wire —
// the server returns `hasApiKey: bool` instead of the encrypted blob)
// plus the optional outbound webhook URL we post campaign events to.

interface DomainRow {
  id: string;
  domainName: string;
  senderDomain: string;
  sendMode: "marketing" | "transactional";
  hasApiKey: boolean;
  hasWebhookSecret: boolean;
  brevoWebhookUrl: string;
  outboundWebhookUrl: string | null;
  hasOutboundWebhookSecret: boolean;
  suspendedAt: string | null;
  createdAt: string;
  brevo: { ok: true; email: string | null; companyName: string | null; planType: string | null; remainingCredits: number | null; fetchedAt: string }
       | { ok: false; reason: string };
}

export default function DomainsPage() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<DomainRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["notify-domains"],
    queryFn: () => api<{ rows: DomainRow[] }>("/api/admin/notify-domains"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/admin/notify-domains/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notify-domains"] }),
  });

  const suspend = useMutation({
    mutationFn: ({ id, suspended }: { id: string; suspended: boolean }) =>
      api(`/api/admin/notify-domains/${id}`, { method: "PATCH", body: JSON.stringify({ suspended }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notify-domains"] }),
  });

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <Globe size={22} /> Domains
        </h1>
        <button onClick={() => setShowAdd(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
          <Plus size={14} /> Add domain
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Each row is one Brevo account authenticated at the listed sender domain.
        Brevo API key signs the outbound API calls; the webhook secret validates the
        inbound delivery / bounce events Brevo posts back. The outbound webhook URL
        (optional) is where this Notify instance forwards campaign events to your server.
      </p>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-5 py-2">Domain</th>
              <th className="text-left px-5 py-2">Sender</th>
              <th className="text-left px-5 py-2">Mode</th>
              <th className="text-left px-5 py-2">Brevo</th>
              <th className="text-left px-5 py-2">Credits</th>
              <th className="text-left px-5 py-2">Outbound webhook</th>
              <th className="text-left px-5 py-2">Status</th>
              <th className="text-right px-5 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100">
                <td className="px-5 py-3 font-medium text-gray-900">{r.domainName}</td>
                <td className="px-5 py-3 text-gray-600">{r.senderDomain}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded ${r.sendMode === "marketing" ? "bg-pink-50 text-pink-700" : "bg-blue-50 text-blue-700"}`}>
                    {r.sendMode}
                  </span>
                </td>
                <td className="px-5 py-3 text-gray-600">
                  <CredBadge label="API key" ok={r.hasApiKey} />
                  <CredBadge label="Webhook secret" ok={r.hasWebhookSecret} className="ml-2" />
                </td>
                <td className="px-5 py-3 text-gray-600">
                  <BrevoCredits b={r.brevo} />
                </td>
                <td className="px-5 py-3 text-gray-600 max-w-[14rem] truncate" title={r.outboundWebhookUrl ?? ""}>
                  {r.outboundWebhookUrl ? (
                    <span className="text-gray-700">{r.outboundWebhookUrl}</span>
                  ) : (
                    <span className="text-gray-400">— not set</span>
                  )}
                </td>
                <td className="px-5 py-3">
                  {r.suspendedAt ? (
                    <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded">Suspended</span>
                  ) : (
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded">Active</span>
                  )}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(r)}
                    title="Edit"
                    className="text-gray-500 hover:text-indigo-600 p-1.5 mr-1">
                    <SettingsIcon size={14} />
                  </button>
                  <button onClick={() => suspend.mutate({ id: r.id, suspended: !r.suspendedAt })}
                    title={r.suspendedAt ? "Resume" : "Suspend"}
                    className="text-gray-500 hover:text-amber-600 p-1.5 mr-1">
                    {r.suspendedAt ? <PlayCircle size={14} /> : <PauseCircle size={14} />}
                  </button>
                  <button onClick={() => {
                      if (confirm(`Delete ${r.domainName}? This removes its Brevo config and all user-domain grants.`)) {
                        remove.mutate(r.id);
                      }
                    }}
                    title="Delete"
                    className="text-gray-500 hover:text-red-600 p-1.5">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {data && data.rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-10 text-center text-gray-400 text-sm">
                  No domains yet. Add one to start sending through Brevo.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && <DomainFormModal onClose={() => setShowAdd(false)} />}
      {editing && <DomainFormModal existing={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function CredBadge({ label, ok, className = "" }: { label: string; ok: boolean; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${ok ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"} ${className}`}>
      {ok ? <Check size={11} /> : <X size={11} />} {label}
    </span>
  );
}

function BrevoCredits({ b }: { b: DomainRow["brevo"] }) {
  if (!b.ok) {
    const reason = b.reason === "no_api_key" ? "API key not set"
                : b.reason === "http_401" ? "Key rejected"
                : b.reason === "http_403" ? "Forbidden"
                : b.reason === "network" ? "Network error"
                : b.reason;
    return <span className="text-xs text-amber-600" title={b.reason}>— {reason}</span>;
  }
  if (b.remainingCredits === null) {
    return <span className="text-xs text-gray-400" title={`Plan: ${b.planType ?? "?"}`}>— no quota</span>;
  }
  const low = b.remainingCredits <= 25;
  return (
    <span className="text-xs" title={`${b.email ?? ""} · ${b.planType ?? ""}`}>
      <span className={low ? "text-red-600 font-medium" : "text-gray-700"}>{b.remainingCredits.toLocaleString()}</span>
      <span className="text-gray-400"> left</span>
    </span>
  );
}

// Create or edit. On create the user enters everything fresh. On edit
// the cred fields stay blank (we never receive the existing values
// from the server) — typing into them rotates the cred, leaving them
// blank keeps the existing one untouched.
function DomainFormModal({ existing, onClose }: { existing?: DomainRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [domainName, setDomainName] = useState(existing?.domainName ?? "");
  const [senderDomain, setSenderDomain] = useState(existing?.senderDomain ?? "");
  const [sendMode, setSendMode] = useState<"marketing" | "transactional">(existing?.sendMode ?? "marketing");
  const [brevoApiKey, setBrevoApiKey] = useState("");
  const [brevoWebhookSecret, setBrevoWebhookSecret] = useState("");
  const [outboundWebhookUrl, setOutboundWebhookUrl] = useState(existing?.outboundWebhookUrl ?? "");
  const [outboundWebhookSecret, setOutboundWebhookSecret] = useState("");
  const [showSecrets, setShowSecrets] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  function autoFillSender() {
    if (domainName && !senderDomain) setSenderDomain(`notify.${domainName}`);
  }

  const mut = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {};
      if (!existing) {
        payload.domainName = domainName;
        payload.senderDomain = senderDomain;
      } else if (senderDomain !== existing.senderDomain) {
        payload.senderDomain = senderDomain;
      }
      if (brevoApiKey) payload.brevoApiKey = brevoApiKey;
      if (brevoWebhookSecret) payload.brevoWebhookSecret = brevoWebhookSecret;
      if (existing) {
        // On edit, send the URL only if it actually changed — pass null
        // explicitly to clear it.
        if (outboundWebhookUrl !== (existing.outboundWebhookUrl ?? "")) {
          payload.outboundWebhookUrl = outboundWebhookUrl || null;
        }
      } else if (outboundWebhookUrl) {
        payload.outboundWebhookUrl = outboundWebhookUrl;
      }
      if (outboundWebhookSecret) payload.outboundWebhookSecret = outboundWebhookSecret;

      if (!existing || sendMode !== existing.sendMode) payload.sendMode = sendMode;

      const path = existing ? `/api/admin/notify-domains/${existing.id}` : "/api/admin/notify-domains";
      const method = existing ? "PATCH" : "POST";
      return api(path, { method, body: JSON.stringify(payload) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notify-domains"] });
      onClose();
    },
    onError: (e: ApiError) => setErr(e.message),
  });

  function copy(text: string, key: string) {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">
            {existing ? `Edit ${existing.domainName}` : "Add domain"}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Brand domain + sender */}
          <Field label="Brand domain" hint="The customer-facing domain you own, e.g. officeease.in">
            <input value={domainName} onChange={(e) => setDomainName(e.target.value.toLowerCase().trim())}
              onBlur={autoFillSender}
              disabled={!!existing}
              placeholder="officeease.in"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500" />
          </Field>

          <Field label="Sender domain" hint="Brevo-authenticated subdomain. Must equal the brand domain or be a subdomain of it.">
            <input value={senderDomain} onChange={(e) => setSenderDomain(e.target.value.toLowerCase().trim())}
              placeholder="notify.officeease.in"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500" />
          </Field>

          <Field label="Send mode"
            hint={sendMode === "marketing"
              ? "Adds List-Unsubscribe header + unsubscribe footer + postal address line. Gmail typically routes to Promotions. Required for newsletters / bulk campaigns."
              : "Drops the unsubscribe header + footer. Gmail more often places these in Primary. Use for 1:1 cold outreach, password resets, receipts. Note: Brevo's open/click tracking is a separate dashboard setting — toggle that in Brevo if you also want Primary-style behavior."}>
            <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden text-sm">
              <button type="button" onClick={() => setSendMode("marketing")}
                className={`px-3 py-1.5 ${sendMode === "marketing" ? "bg-pink-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}>
                Marketing
              </button>
              <button type="button" onClick={() => setSendMode("transactional")}
                className={`px-3 py-1.5 border-l border-gray-300 ${sendMode === "transactional" ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}>
                Transactional
              </button>
            </div>
          </Field>

          {/* Brevo creds */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-900">Brevo account</h4>
              <button type="button" onClick={() => setShowSecrets((v) => !v)}
                className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
                {showSecrets ? <EyeOff size={12} /> : <Eye size={12} />} {showSecrets ? "Hide" : "Reveal typing"}
              </button>
            </div>

            <Field label="API key" hint={existing && existing.hasApiKey
              ? "Leave blank to keep the existing key. Paste a new value to rotate."
              : "From Brevo → SMTP & API → API keys. Starts with xkeysib-."}>
              <input type={showSecrets ? "text" : "password"} value={brevoApiKey}
                onChange={(e) => setBrevoApiKey(e.target.value)}
                placeholder={existing?.hasApiKey ? "•••••••••• (stored)" : "xkeysib-…"}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500" />
            </Field>

            {existing && (
              <Field label="Webhook URL for Brevo" hint="Paste into Brevo → Transactional → Settings → Webhook.">
                <div className="flex gap-2">
                  <input readOnly value={existing.brevoWebhookUrl}
                    className="flex-1 border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-xs font-mono text-gray-700" />
                  <button type="button" onClick={() => copy(existing.brevoWebhookUrl, "brevo-url")}
                    className="px-3 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 inline-flex items-center gap-1">
                    {copied === "brevo-url" ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                  </button>
                </div>
              </Field>
            )}

            <Field label="Webhook secret" hint={existing && existing.hasWebhookSecret
              ? "Leave blank to keep. Paste a new value to rotate."
              : "Brevo signs incoming webhook calls with this. Must match the value set in Brevo's webhook config."}>
              <input type={showSecrets ? "text" : "password"} value={brevoWebhookSecret}
                onChange={(e) => setBrevoWebhookSecret(e.target.value)}
                placeholder={existing?.hasWebhookSecret ? "•••••••••• (stored)" : "Shared signing secret"}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500" />
            </Field>
          </div>

          {/* Outbound webhook */}
          <div className="border-t border-gray-100 pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Outbound webhook (optional)</h4>
            <p className="text-xs text-gray-500 mb-3">
              Where this Notify instance posts campaign events (delivered / bounced / opened / clicked / unsubscribed).
              Each request is HMAC-signed with the secret using SHA-256 — verify on your side before trusting the payload.
            </p>

            <Field label="Webhook URL" hint="https://your-server.example.com/notify-events">
              <input value={outboundWebhookUrl}
                onChange={(e) => setOutboundWebhookUrl(e.target.value.trim())}
                placeholder="Leave blank to disable"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500" />
            </Field>

            <Field label="Signing secret" hint={existing && existing.hasOutboundWebhookSecret
              ? "Leave blank to keep. Paste a new value to rotate."
              : "Generate any random string; both sides must hold the same secret."}>
              <input type={showSecrets ? "text" : "password"} value={outboundWebhookSecret}
                onChange={(e) => setOutboundWebhookSecret(e.target.value)}
                placeholder={existing?.hasOutboundWebhookSecret ? "•••••••••• (stored)" : "Random opaque string"}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500" />
            </Field>
          </div>

          {err && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</p>}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={() => { setErr(null); mut.mutate(); }}
            disabled={mut.isPending || (!existing && (!domainName || !senderDomain))}
            className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-1.5 rounded-lg">
            {mut.isPending ? "Saving…" : existing ? "Save changes" : "Add domain"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}
